/**
 * LX HEV - Generateur de packs batterie equilibres (logique PAR BLOC = paire de 2 cellules)
 *
 * Un pack de N cellules = N/2 blocs (28 -> 14 blocs, 32 -> 16 blocs).
 * Donnees utilisees : ID, Capacite (mAh), Resistance IR, Serie (info), Disponibilite (DISPO uniquement).
 * Le temps de decharge est INFORMATIF (jamais dans le score) ; l'Etat n'est jamais utilise.
 *
 * ARCHITECTURE (deux niveaux) :
 *  NIVEAU 1 - contraintes dures : delta global > 1000 mAh ou une paire > 500 mAh = pack invalide
 *             (penalite massive, jamais compensable). Les solutions sont ensuite comparees
 *             HIERARCHIQUEMENT (cles par paliers) : delta global -> pire delta interne ->
 *             delta interne moyen -> delta IR global -> pire delta IR interne -> moyenne IR.
 *  NIVEAU 2 - le score pondere ne departage que des solutions deja proches (meme palier).
 *
 * RECHERCHE : pre-selection de fenetres de capacite (multi-start) -> recuit simule sur
 *  (echange de cellules entre blocs / remplacement par une cellule du stock) -> descente
 *  exhaustive du voisinage. Generateur pseudo-aleatoire a graine deterministe : memes donnees =
 *  meme resultat. Le placement physique est optimise APRES la formation des paires.
 *
 * PROPOSITIONS : Pack A (meilleur equilibre), Pack B (capacite elevee sous contrainte d'equilibre),
 *  Pack C (alternative disjointe/differente). Les doublons et les mauvais packs ne sont pas affiches.
 */

/* ── Constantes metier ──────────────────────────────────────────────────── */

const LIMITS = {
  hardGlobalCap: 1000,   // > : PACK NON EQUILIBRE (limite absolue de secours, pas une cible)
  criticalInternalCap: 500, // paire > : critique / pack non equilibre
  warnInternalCap: 300,  // alerte bloc
  warnInternalIR: 1.0,   // alerte bloc
};

const STATUS_ORDER = ["Excellent", "Tres bon", "Bon", "Tolerable", "Mauvais", "Critique"];
const STATUS_RANK = Object.fromEntries(STATUS_ORDER.map((s, i) => [s, i]));

// Seuils verdict (tous les criteres doivent etre respectes).
const VERDICT_LEVELS = [
  { name: "Excellent", dCap: 300, intCap: 150, dIR: 1.0, intIR: 0.5 },
  { name: "Tres bon", dCap: 500, intCap: 200, dIR: 1.5, intIR: 0.7 },
  { name: "Bon", dCap: 800, intCap: 300, dIR: 2.0, intIR: 1.0 },
];
const VERDICT_QUALITY_CAP = { Excellent: 100, "Tres bon": 90, Bon: 75, "A revoir": 55, "Non equilibre": 30 };

// Paliers de la comparaison hierarchique (tolerance en dessous de laquelle deux valeurs sont "proches").
const BANDS = { dCap: 40, maxInt: 40, meanInt: 25, dIR: 0.3, maxIR: 0.25, meanIR: 0.15 };

// Paliers de verdict utilises comme premieres cles de comparaison (delta global, pire delta interne).
const CAP_LEVELS = [300, 500, 800, 1000];
const INT_LEVELS = [150, 200, 300, 500];
function levelOf(v, levels) { let i = 0; while (i < levels.length && v > levels[i]) i++; return i; }

// Poids de l'energie (guide de la recherche + departage final), a l'ordre des priorites.
const W = { dCap: 1, maxInt: 1.5, meanInt: 0.4, dIR: 60, maxIR: 40, meanIR: 15 };

// Budgets d'optimisation en ITERATIONS (jamais en temps : garantit la reproductibilite).
const BUDGET = {
  seedsA: 10, itersA: 20000,
  seedsB: 6, itersB: 14000,
  seedsC: 6, itersC: 14000,
  polishPasses: 12,
  placementStarts: 14,
};

// Packs "exceptionnels" : la recherche s'arrete des qu'on atteint ce niveau.
const EXCEPTIONAL = { dCap: 120, maxInt: 80, dIR: 0.4, maxIR: 0.25 };

const DEV = typeof process !== "undefined" && process.env && process.env.NODE_ENV === "development";

/* ── Configuration & normalisation ──────────────────────────────────────── */

function buildConfig(options) {
  const packSize = options.packSize || 28;
  return {
    packSize,
    blocks: packSize / 2,
    onlyAvailable: options.onlyAvailable !== false,
    minCapacity: options.minCapacity != null && options.minCapacity !== "" ? Number(options.minCapacity) : null,
    seed: options.seed != null ? Number(options.seed) : null,
    debug: options.debug != null ? !!options.debug : DEV,
    internalCapWarn: options.internalCapWarn != null ? Number(options.internalCapWarn) : LIMITS.warnInternalCap,
  };
}

function firstDefined(...vals) {
  for (const v of vals) if (v !== null && v !== undefined && v !== "") return v;
  return undefined;
}
function toNumber(v) {
  if (v === undefined || v === null || v === "") return NaN;
  return typeof v === "string" ? Number(v.replace(",", ".")) : Number(v);
}

function normalizeCell(cell) {
  const t = toNumber(firstDefined(cell.T_Dech, cell.dischargeTime, cell.t_dech, cell.discharge_time));
  return {
    id: cell.id || cell.ID || cell.cell_number || cell.Cellule || cell.name,
    Serie: cell.Serie || cell.series || "",
    Cap_Ah: toNumber(firstDefined(cell.Cap_Ah, cell.capacity, cell.Capacity, cell.capacity_ah)),
    Resistance: toNumber(firstDefined(cell.Resistance, cell.IR, cell.ir, cell.internal_resistance)),
    T_Dech: isValidNumber(t) ? t : 0, // optionnel : info d'affichage seulement
    Disponible: firstDefined(cell.Disponible, cell.available, cell.status, cell.availability) || "DISPO",
    original: cell,
  };
}

// Filtre (DISPO + capacite/IR numeriques valides + capacite min), dedoublonne par id, tri deterministe.
function prepareCells(inputCells, config) {
  const seen = new Set();
  let cells = inputCells
    .map(normalizeCell)
    .filter(c => (!config.onlyAvailable || String(c.Disponible).toUpperCase().startsWith("DISPO")))
    .filter(c => isValidNumber(c.Cap_Ah) && c.Cap_Ah > 0 && isValidNumber(c.Resistance) && c.Resistance > 0)
    .filter(c => {
      // Une cellule physique ne peut jamais etre utilisee deux fois : on dedoublonne par id ET par
      // numero de cellule (la base peut contenir deux enregistrements pour le meme numero).
      const keys = [c.id != null ? `id:${c.id}` : null, c.original?.cell_number ? `n:${String(c.original.cell_number).trim().toUpperCase()}` : null].filter(Boolean);
      if (keys.some(k => seen.has(k))) return false;
      keys.forEach(k => seen.add(k));
      return true;
    });
  if (config.minCapacity != null && isValidNumber(config.minCapacity)) {
    cells = cells.filter(c => c.Cap_Ah >= config.minCapacity);
  }
  return cells.sort((a, b) =>
    a.Cap_Ah - b.Cap_Ah || a.Resistance - b.Resistance || String(a.id).localeCompare(String(b.id)));
}

/* ── Aleatoire deterministe ─────────────────────────────────────────────── */

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Graine derivee des donnees (+ options) : memes donnees => meme resultat.
function hashSeed(cells, config, salt) {
  let h = 2166136261 ^ (config.seed != null ? config.seed : 0) ^ (config.packSize * 7919) ^ (salt * 104729);
  for (const c of cells) {
    h = Math.imul(h ^ Math.round(c.Cap_Ah * 10), 16777619);
    h = Math.imul(h ^ Math.round(c.Resistance * 1000), 16777619);
    const id = String(c.id);
    for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  }
  return h >>> 0;
}

/* ── Metriques d'un jeu de paires (chemin chaud) ────────────────────────── */

// pa[k], pb[k] = indices (dans le tableau trie) des deux cellules du bloc k.
function newMetrics() {
  return { dCap: 0, maxInt: 0, meanInt: 0, minInt: 0, sumInt: 0, dIR: 0, maxIR: 0, meanIR: 0, sumCap: 0, key: new Array(9).fill(0), energy: 0 };
}

function evaluate(cells, pa, pb, m) {
  const P = pa.length;
  let minC = Infinity, maxC = -Infinity, minI = Infinity, maxI = -Infinity;
  let sumDc = 0, maxDc = 0, minDc = Infinity, sumDi = 0, maxDi = 0, sumCap = 0;
  for (let k = 0; k < P; k++) {
    const a = cells[pa[k]], b = cells[pb[k]];
    const ct = a.Cap_Ah + b.Cap_Ah;
    const it = a.Resistance + b.Resistance;
    const dc = Math.abs(a.Cap_Ah - b.Cap_Ah);
    const di = Math.abs(a.Resistance - b.Resistance);
    if (ct < minC) minC = ct;
    if (ct > maxC) maxC = ct;
    if (it < minI) minI = it;
    if (it > maxI) maxI = it;
    sumDc += dc; if (dc > maxDc) maxDc = dc; if (dc < minDc) minDc = dc;
    sumDi += di; if (di > maxDi) maxDi = di;
    sumCap += ct;
  }
  m.dCap = maxC - minC;
  m.maxInt = maxDc;
  m.minInt = minDc;
  m.sumInt = sumDc;
  m.meanInt = sumDc / P;
  m.dIR = maxI - minI;
  m.maxIR = maxDi;
  m.meanIR = sumDi / P;
  m.sumCap = sumCap;
  const k = m.key;
  k[0] = (m.dCap > LIMITS.hardGlobalCap || m.maxInt > LIMITS.criticalInternalCap) ? 1 : 0; // validite (niveau 1)
  k[1] = levelOf(m.dCap, CAP_LEVELS);       // palier de verdict : delta global
  k[2] = levelOf(m.maxInt, INT_LEVELS);     // palier de verdict : pire delta interne
  k[3] = Math.floor(m.dCap / BANDS.dCap);
  k[4] = Math.floor(m.maxInt / BANDS.maxInt);
  k[5] = Math.floor(m.meanInt / BANDS.meanInt);
  k[6] = Math.floor(m.dIR / BANDS.dIR);
  k[7] = Math.floor(m.maxIR / BANDS.maxIR);
  k[8] = Math.floor(m.meanIR / BANDS.meanIR);
  m.energy = energyBalance(m);
  return m;
}

function cloneMetrics(m) { return { ...m, key: m.key.slice() }; }

// Energie d'equilibre : guide de recherche + departage. Penalites dures non compensables.
function energyBalance(m) {
  let e = W.dCap * m.dCap + W.maxInt * m.maxInt + W.meanInt * m.meanInt
        + W.dIR * m.dIR + W.maxIR * m.maxIR + W.meanIR * m.meanIR;
  if (m.dCap > 300) e += (m.dCap - 300) * 0.5;
  if (m.dCap > 500) e += (m.dCap - 500) * 0.5;
  if (m.maxInt > 150) e += (m.maxInt - 150) * 0.8;
  if (m.maxInt > 300) e += (m.maxInt - 300) * 0.5;
  if (m.dCap > LIMITS.hardGlobalCap) e += 1e5 + (m.dCap - LIMITS.hardGlobalCap) * 50;
  if (m.maxInt > LIMITS.criticalInternalCap) e += 1e5 + (m.maxInt - LIMITS.criticalInternalCap) * 50;
  return e;
}

// Comparaison hierarchique : cles par paliers, puis energie continue. < 0 => a meilleur que b.
function cmpBalance(a, b) {
  for (let i = 0; i < a.key.length; i++) if (a.key[i] !== b.key[i]) return a.key[i] - b.key[i];
  return a.energy - b.energy;
}

// Mode "capacite elevee" : on cherche la plus forte capacite moyenne DANS des bornes d'equilibre.
function bounds(ref) {
  return {
    dCap: Math.max(400, ref.dCap * 1.5),
    maxInt: Math.max(200, ref.maxInt * 1.3),
    dIR: Math.max(1.5, ref.dIR * 1.3),
    maxIR: Math.max(0.7, ref.maxIR * 1.3),
  };
}
function excess(m, bd) {
  return Math.max(0, m.dCap - bd.dCap) + 1.5 * Math.max(0, m.maxInt - bd.maxInt)
       + 60 * Math.max(0, m.dIR - bd.dIR) + 80 * Math.max(0, m.maxIR - bd.maxIR);
}

// Un "mode" = comment juger une solution (energie pour le recuit + comparateur pour garder la meilleure).
function makeMode(name, ref, cellCount) {
  if (name === "capacity") {
    const bd = bounds(ref);
    const score = (m) => 0.25 * m.energy + 20 * excess(m, bd) - 2 * (m.sumCap / cellCount);
    return {
      name, energy: score,
      cmp: (a, b) => {
        const ea = excess(a, bd), eb = excess(b, bd);
        if (a.key[0] !== b.key[0]) return a.key[0] - b.key[0];
        if ((ea > 0) !== (eb > 0)) return ea > 0 ? 1 : -1;
        if (ea > 0) return ea - eb;
        const d = b.sumCap - a.sumCap;
        if (Math.abs(d) > cellCount * 3) return d; // > 3 mAh de moyenne cellule d'ecart
        return cmpBalance(a, b);
      },
    };
  }
  return { name: "balance", energy: (m) => m.energy, cmp: cmpBalance };
}

function isExceptional(m) {
  return m.key[0] === 0 && m.dCap <= EXCEPTIONAL.dCap && m.maxInt <= EXCEPTIONAL.maxInt
    && m.dIR <= EXCEPTIONAL.dIR && m.maxIR <= EXCEPTIONAL.maxIR;
}

/* ── Recherche : etat, graines, recuit simule, descente ─────────────────── */

// `usable[i]` = cellule i utilisable (false si exclue pour Pack C). `used[i]` = deja dans l'etat courant.
function makeState(pa, pb, n) {
  const used = new Uint8Array(n);
  for (let k = 0; k < pa.length; k++) { used[pa[k]] = 1; used[pb[k]] = 1; }
  return { pa: Int32Array.from(pa), pb: Int32Array.from(pb), used };
}

// Graine : fenetre de N cellules consecutives (par capacite) formant des paires "pliees"
// (forte + faible) ; le recuit corrige ensuite le compromis delta global / delta interne.
function seedFromWindow(cells, usableIdx, start, P) {
  const win = usableIdx.slice(start, start + 2 * P);
  const pa = [], pb = [];
  // repli partiel : melange adjacent / plie pour partir pres d'un bon compromis
  for (let k = 0; k < P; k++) { pa.push(win[k]); pb.push(win[2 * P - 1 - k]); }
  return { pa, pb };
}

function seedAdjacent(usableIdx, start, P) {
  const win = usableIdx.slice(start, start + 2 * P);
  const pa = [], pb = [];
  for (let k = 0; k < P; k++) { pa.push(win[2 * k]); pb.push(win[2 * k + 1]); }
  return { pa, pb };
}

// Pre-classement des fenetres : meilleur des deux pairages simples (adjacent / plie) selon l'energie.
function rankWindows(cells, usableIdx, P, mode, scratch) {
  const out = [];
  for (let s = 0; s + 2 * P <= usableIdx.length; s++) {
    const f = seedFromWindow(cells, usableIdx, s, P);
    const a = seedAdjacent(usableIdx, s, P);
    const ef = mode.energy(evaluate(cells, f.pa, f.pb, scratch));
    const ea = mode.energy(evaluate(cells, a.pa, a.pb, scratch));
    out.push({ start: s, seed: ef <= ea ? f : a, e: Math.min(ef, ea) });
  }
  return out;
}

// Choisit jusqu'a `count` fenetres bien classees et espacees (pas de quasi-doublons).
function pickSeeds(ranked, count, spacing) {
  const sorted = [...ranked].sort((a, b) => a.e - b.e || a.start - b.start);
  const picked = [];
  for (const w of sorted) {
    if (picked.every(p => Math.abs(p.start - w.start) >= spacing)) picked.push(w);
    if (picked.length >= count) break;
  }
  return picked;
}

// Candidat de remplacement : cellule libre proche (en rang de capacite) de celle retiree.
function pickReplacement(state, usable, around, rng, n) {
  for (let tries = 0; tries < 12; tries++) {
    const span = rng() < 0.75 ? 12 : 60;
    const idx = Math.max(0, Math.min(n - 1, around + Math.floor((rng() * 2 - 1) * span)));
    if (usable[idx] && !state.used[idx]) return idx;
  }
  return -1;
}

function pairExtremes(cells, state, P) {
  let iMax = 0, iMin = 0, iInt = 0, cMax = -Infinity, cMin = Infinity, dMax = -1;
  for (let k = 0; k < P; k++) {
    const a = cells[state.pa[k]], b = cells[state.pb[k]];
    const c = a.Cap_Ah + b.Cap_Ah;
    const d = Math.abs(a.Cap_Ah - b.Cap_Ah);
    if (c > cMax) { cMax = c; iMax = k; }
    if (c < cMin) { cMin = c; iMin = k; }
    if (d > dMax) { dMax = d; iInt = k; }
  }
  return [iMax, iMin, iInt];
}

// Recuit simule (graine, budget en iterations). Retourne la meilleure solution rencontree.
function anneal(ctx, seed, mode, iters, rng) {
  const { cells, usable, n, P, counters } = ctx;
  const state = makeState(seed.pa, seed.pb, n);
  const m = newMetrics();
  const cur = evaluate(cells, state.pa, state.pb, m);
  let curE = mode.energy(cur);
  let best = { pa: state.pa.slice(), pb: state.pb.slice(), m: cloneMetrics(cur) };
  const T0 = 45, T1 = 0.4;
  const trial = newMetrics();

  for (let it = 0; it < iters; it++) {
    const T = T0 * Math.pow(T1 / T0, it / iters);
    counters.iterations++;
    let undo;
    const useExt = rng() < 0.35;
    const ext = useExt ? pairExtremes(cells, state, P) : null;

    if (rng() < 0.55 || P < 2) {
      // Remplacement : une cellule du pack <-> une cellule libre proche
      const k = useExt ? ext[Math.floor(rng() * 3)] : Math.floor(rng() * P);
      const side = rng() < 0.5;
      const arr = side ? state.pa : state.pb;
      const old = arr[k];
      const repl = pickReplacement(state, usable, old, rng, n);
      if (repl < 0) continue;
      arr[k] = repl; state.used[old] = 0; state.used[repl] = 1;
      undo = () => { arr[k] = old; state.used[repl] = 0; state.used[old] = 1; };
    } else {
      // Echange d'une cellule entre deux blocs (couvre aussi la reconstruction de deux paires)
      const i = useExt ? ext[Math.floor(rng() * 3)] : Math.floor(rng() * P);
      let j = Math.floor(rng() * (P - 1)); if (j >= i) j++;
      const ai = rng() < 0.5 ? state.pa : state.pb;
      const aj = rng() < 0.5 ? state.pa : state.pb;
      const ci = ai[i], cj = aj[j];
      ai[i] = cj; aj[j] = ci;
      undo = () => { ai[i] = ci; aj[j] = cj; };
    }

    counters.evaluations++;
    evaluate(cells, state.pa, state.pb, trial);
    const e = mode.energy(trial);
    const dE = e - curE;
    if (dE <= 0 || rng() < Math.exp(-dE / T)) {
      curE = e;
      if (mode.cmp(trial, best.m) < 0) {
        best = { pa: state.pa.slice(), pb: state.pb.slice(), m: cloneMetrics(trial) };
        if (mode.name === "balance" && isExceptional(best.m)) break; // early stopping
      }
    } else {
      undo();
    }
  }
  return best;
}

// Descente exhaustive sur le voisinage (echanges inter-blocs + remplacements), comparateur hierarchique.
function polish(ctx, sol, mode) {
  const { cells, usable, n, P, counters } = ctx;
  const state = makeState(sol.pa, sol.pb, n);
  const trial = newMetrics();
  let best = cloneMetrics(evaluate(cells, state.pa, state.pb, newMetrics()));
  const arrays = [state.pa, state.pb];

  for (let pass = 0; pass < BUDGET.polishPasses; pass++) {
    let improved = false;
    // 1. echanges entre blocs
    for (let i = 0; i < P; i++) for (let j = i + 1; j < P; j++) {
      for (const ai of arrays) for (const aj of arrays) {
        const ci = ai[i], cj = aj[j];
        ai[i] = cj; aj[j] = ci;
        counters.evaluations++;
        evaluate(cells, state.pa, state.pb, trial);
        if (mode.cmp(trial, best) < 0) { best = cloneMetrics(trial); improved = true; }
        else { ai[i] = ci; aj[j] = cj; }
      }
    }
    // 2. remplacement par une cellule du stock (fenetre de rang +-40)
    for (let k = 0; k < P; k++) for (const arr of arrays) {
      const old = arr[k];
      for (let c = Math.max(0, old - 40); c <= Math.min(n - 1, old + 40); c++) {
        if (!usable[c] || state.used[c]) continue;
        arr[k] = c; state.used[old] = 0; state.used[c] = 1;
        counters.evaluations++;
        evaluate(cells, state.pa, state.pb, trial);
        if (mode.cmp(trial, best) < 0) { best = cloneMetrics(trial); improved = true; break; }
        arr[k] = old; state.used[c] = 0; state.used[old] = 1;
      }
    }
    if (!improved) break;
  }
  return { pa: state.pa.slice(), pb: state.pb.slice(), m: best };
}

// Recherche complete d'un pack pour un mode donne : multi-start + recuit + descente.
function searchPack(ctx, mode, seeds, iters, rngSalt, extraSeeds = []) {
  const rng = mulberry32(ctx.seedBase + rngSalt);
  let best = null;
  const consider = (s) => {
    if (!best || mode.cmp(s.m, best.m) < 0) {
      best = s;
      if (ctx.config.debug) console.debug(`[balancer:${mode.name}] amelioration : delta global ${s.m.dCap.toFixed(0)} mAh, pire interne ${s.m.maxInt.toFixed(0)} mAh`);
    }
  };
  for (const sd of [...extraSeeds, ...seeds]) {
    ctx.counters.candidates++;
    const annealed = anneal(ctx, sd, mode, iters, rng);
    consider(polish(ctx, annealed, mode));
    if (mode.name === "balance" && best && isExceptional(best.m)) break;
  }
  return best;
}

/* ── Blocs ──────────────────────────────────────────────────────────────── */

function capStatusOf(delta) {
  if (delta <= 100) return "Excellent";
  if (delta <= 150) return "Tres bon";
  if (delta <= 250) return "Bon";
  if (delta <= 300) return "Tolerable";
  if (delta <= 500) return "Mauvais";
  return "Critique";
}
function irStatusOf(delta) {
  if (delta <= 0.30) return "Excellent";
  if (delta <= 0.50) return "Tres bon";
  if (delta <= 0.80) return "Bon";
  if (delta <= 1.00) return "Tolerable";
  return "Mauvais";
}

function computeBlock(x, y, config) {
  // Cellule A = la plus forte en capacite (affichage coherent).
  const [A, B] = x.Cap_Ah >= y.Cap_Ah ? [x, y] : [y, x];
  const deltaCapacity = Math.abs(A.Cap_Ah - B.Cap_Ah);
  const deltaIR = Math.abs(A.Resistance - B.Resistance);
  const deltaTime = Math.abs(A.T_Dech - B.T_Dech);
  const capStatus = capStatusOf(deltaCapacity);
  const irStatus = irStatusOf(deltaIR);
  // Le statut du bloc = le PLUS MAUVAIS entre capacite et IR.
  const blockStatus = STATUS_RANK[capStatus] >= STATUS_RANK[irStatus] ? capStatus : irStatus;

  const alerts = [];
  if (deltaCapacity > LIMITS.criticalInternalCap) alerts.push({ level: "critical", text: `CRITIQUE : delta capacite interne ${round(deltaCapacity, 0)} mAh (> ${LIMITS.criticalInternalCap})` });
  else if (deltaCapacity > LIMITS.warnInternalCap) alerts.push({ level: "warning", text: `Delta capacite interne ${round(deltaCapacity, 0)} mAh (> ${LIMITS.warnInternalCap})` });
  if (deltaIR > LIMITS.warnInternalIR) alerts.push({ level: "warning", text: `Delta IR interne ${round(deltaIR, 2)} (> ${LIMITS.warnInternalIR})` });

  // Info seule : capacites tres proches mais temps de decharge anormalement differents.
  const measureCheck = deltaCapacity <= 150 && A.T_Dech > 0 && B.T_Dech > 0
    && Math.abs(A.T_Dech - B.T_Dech) / Math.max(A.T_Dech, B.T_Dech) > 0.2;
  if (measureCheck) alerts.push({ level: "info", text: "Mesure a verifier (temps de decharge anormalement different)" });

  return {
    cellA: A,
    cellB: B,
    capacityA: round(A.Cap_Ah, 2),
    capacityB: round(B.Cap_Ah, 2),
    capacityTotal: round(A.Cap_Ah + B.Cap_Ah, 2),
    capacityMean: round((A.Cap_Ah + B.Cap_Ah) / 2, 2),
    irA: round(A.Resistance, 3),
    irB: round(B.Resistance, 3),
    irTotal: round(A.Resistance + B.Resistance, 3),
    irMean: round((A.Resistance + B.Resistance) / 2, 3),
    timeA: round(A.T_Dech, 3),
    timeB: round(B.T_Dech, 3),
    timeMean: round((A.T_Dech + B.T_Dech) / 2, 3),
    deltaCapacity: round(deltaCapacity, 2),
    deltaIR: round(deltaIR, 3),
    deltaTime: round(deltaTime, 3),
    pairScore: round(deltaCapacity * 3 + deltaIR * 100, 3),
    capStatus,
    irStatus,
    blockStatus,
    critical: deltaCapacity > LIMITS.criticalInternalCap,
    measureCheck,
    alerts,
    overWarn: config ? deltaCapacity > config.internalCapWarn : false,
  };
}

// Robustesse d'un bloc pour le placement : forte capacite, faibles ecarts internes.
function blockPerformance(b) {
  return b.capacityTotal - b.irTotal * 50 - b.deltaCapacity * 0.5 - b.deltaIR * 50;
}

/* ── Placement physique (apres validation electrique des paires) ────────── */

function zigzagOrder(n) {
  const order = [];
  let l = 1, r = n;
  while (l <= r) {
    order.push(l++);
    if (l <= r) order.push(r--);
  }
  return order;
}

// Cout de placement : ruptures entre voisins, plus gros saut, symetrie G/D, progression vers le centre
// (forts aux extremites, faibles au centre), blocs les moins homogenes plutot au centre.
function placementCost(seq, delta) {
  const P = seq.length;
  let rupt = 0, maxJump = 0;
  for (let k = 0; k < P - 1; k++) {
    const j = Math.abs(seq[k] - seq[k + 1]);
    rupt += j; if (j > maxJump) maxJump = j;
  }
  let asym = 0;
  for (let k = 0; k < Math.floor(P / 2); k++) asym += Math.abs(seq[k] - seq[P - 1 - k]);
  const half = Math.floor(P / 2);
  let valley = 0;
  for (let k = 0; k < half - 1; k++) valley += Math.max(0, seq[k + 1] - seq[k]);
  for (let k = half; k < P - 1; k++) valley += Math.max(0, seq[k] - seq[k + 1]);
  let homog = 0;
  const mid = (P - 1) / 2;
  for (let k = 0; k < P; k++) homog += (Math.abs(k - mid) / Math.max(mid, 1)) * delta[k];
  return rupt + 3 * maxJump + 0.5 * asym + valley + 0.3 * homog;
}

// Teste plusieurs permutations (depart miroir + departs aleatoires, descente par echanges de blocs).
function optimizePlacement(blocks, rng) {
  const P = blocks.length;
  const caps = blocks.map(b => b.capacityTotal);
  const dlt = blocks.map(b => b.deltaCapacity);
  const costOf = (perm) => placementCost(perm.map(i => caps[i]), perm.map(i => dlt[i]));
  const descend = (perm) => {
    let cost = costOf(perm);
    for (let guard = 0; guard < 60; guard++) {
      let improved = false;
      for (let i = 0; i < P; i++) for (let j = i + 1; j < P; j++) {
        [perm[i], perm[j]] = [perm[j], perm[i]];
        const c = costOf(perm);
        if (c < cost - 1e-9) { cost = c; improved = true; } else [perm[i], perm[j]] = [perm[j], perm[i]];
      }
      if (!improved) break;
    }
    return { perm, cost };
  };

  // Depart miroir : les plus robustes aux extremites, progression vers le centre.
  const byStrength = blocks.map((_, i) => i).sort((a, b) => blockPerformance(blocks[b]) - blockPerformance(blocks[a]) || a - b);
  const mirror = new Array(P);
  zigzagOrder(P).forEach((pos, rank) => { mirror[pos - 1] = byStrength[rank]; });

  let best = descend(mirror.slice());
  for (let s = 0; s < BUDGET.placementStarts; s++) {
    const perm = blocks.map((_, i) => i);
    for (let i = P - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [perm[i], perm[j]] = [perm[j], perm[i]]; }
    const r = descend(perm);
    if (r.cost < best.cost - 1e-9) best = r;
  }
  return best; // { perm: positions -> index bloc, cost }
}

/* ── Statistiques, verdict, qualite ─────────────────────────────────────── */

function computeVerdict(dCap, intCapMax, dIR, intIRMax) {
  if (dCap > LIMITS.hardGlobalCap || intCapMax > LIMITS.criticalInternalCap) return "Non equilibre";
  for (const l of VERDICT_LEVELS) {
    if (dCap <= l.dCap && intCapMax <= l.intCap && dIR <= l.dIR && intIRMax <= l.intIR) return l.name;
  }
  return "A revoir";
}

function clamp(x, lo, hi) { return Math.max(lo, Math.min(hi, x)); }

// Note d'une metrique (0..100) calee sur les paliers : <= ex -> 100..90, <= tb -> 90..75, <= bon -> 75..50, <= max -> 50..0.
function gradeScore(v, { ex, tb, bon, max: mx }) {
  if (v <= ex) return 100 - 10 * (v / ex);
  if (v <= tb) return 90 - 15 * ((v - ex) / (tb - ex));
  if (v <= bon) return 75 - 25 * ((v - tb) / (bon - tb));
  if (v <= mx) return 50 - 50 * ((v - bon) / (mx - bon));
  return 0;
}

// 40 % delta global | 25 % pire interne | 15 % moyenne interne | 10 % IR | 10 % placement.
// Plafonnee par le verdict : un pack avec une paire catastrophique ne peut jamais etre "excellent".
function computeQuality(s, verdict) {
  const q =
      0.40 * gradeScore(s.dCap, { ex: 300, tb: 500, bon: 800, max: 1500 })
    + 0.25 * gradeScore(s.intMax, { ex: 150, tb: 200, bon: 300, max: 500 })
    + 0.15 * gradeScore(s.intMean, { ex: 75, tb: 100, bon: 150, max: 300 })
    + 0.10 * (0.5 * gradeScore(s.dIR, { ex: 1.0, tb: 1.5, bon: 2.0, max: 3.0 }) + 0.5 * gradeScore(s.irIntMax, { ex: 0.5, tb: 0.7, bon: 1.0, max: 1.5 }))
    + 0.10 * gradeScore(s.maxJump, { ex: 100, tb: 200, bon: 350, max: 700 });
  return Math.round(Math.min(clamp(q, 0, 100), VERDICT_QUALITY_CAP[verdict]));
}

function packStats(blocks, placement, config) {
  const capT = blocks.map(b => b.capacityTotal);
  const irT = blocks.map(b => b.irTotal);
  const tM = blocks.map(b => b.timeMean);
  const intCap = blocks.map(b => b.deltaCapacity);
  const intIR = blocks.map(b => b.deltaIR);
  const dCap = round(max(capT) - min(capT), 2);
  const dIR = round(max(irT) - min(irT), 3);
  const intCapMax = round(max(intCap), 2);
  const intIRMax = round(max(intIR), 3);

  const physical = [...blocks].sort((a, b) => a.physicalPosition - b.physicalPosition);
  let maxJump = 0;
  for (let k = 0; k < physical.length - 1; k++) maxJump = Math.max(maxJump, Math.abs(physical[k].capacityTotal - physical[k + 1].capacityTotal));

  const verdict = computeVerdict(dCap, intCapMax, dIR, intIRMax);
  const qualityPercent = computeQuality({ dCap, intMax: intCapMax, intMean: average(intCap), dIR, irIntMax: intIRMax, maxJump }, verdict);
  const weakest = blocks.reduce((a, b) => (b.capacityTotal < a.capacityTotal ? b : a));
  const strongest = blocks.reduce((a, b) => (b.capacityTotal > a.capacityTotal ? b : a));
  const warning = verdict === "Non equilibre"
    ? (dCap > LIMITS.hardGlobalCap
        ? `Pack non equilibre : delta global ${dCap} mAh > ${LIMITS.hardGlobalCap} mAh`
        : `Pack non equilibre : un bloc a ${intCapMax} mAh d'ecart interne (> ${LIMITS.criticalInternalCap} mAh)`)
    : null;

  return {
    verdict,
    qualityPercent,
    warning,
    level: verdict, // conserve pour compat (description/stockage du pack)
    scoreTotal: round(energyBalance({ dCap, maxInt: intCapMax, meanInt: average(intCap), dIR, maxIR: intIRMax, meanIR: average(intIR) }), 2),
    avgCellCapacity: round(average(blocks.flatMap(b => [b.capacityA, b.capacityB])), 2),
    avgBlockCapacity: round(average(capT), 2),
    weakestBlock: { blockNumber: weakest.blockNumber, capacity: weakest.capacityTotal },
    strongestBlock: { blockNumber: strongest.blockNumber, capacity: strongest.capacityTotal },
    capacityTotal: { min: round(min(capT), 2), max: round(max(capT), 2), delta: dCap },
    irTotal: { min: round(min(irT), 3), max: round(max(irT), 3), delta: dIR },
    timeMean: { min: round(min(tM), 3), max: round(max(tM), 3), delta: round(max(tM) - min(tM), 3) },
    internalCap: { min: round(min(intCap), 2), mean: round(average(intCap), 2), max: intCapMax, sum: round(sum(intCap), 2) },
    internalIR: { mean: round(average(intIR), 3), max: intIRMax },
    internalCapDeltaMax: intCapMax,   // alias historique
    internalIRDeltaMax: intIRMax,     // alias historique
    placementScore: round(placement.cost, 1),
    maxNeighborJump: round(maxJump, 2),
    criticalBlocks: blocks.filter(b => b.critical).length,
    measureChecks: blocks.filter(b => b.measureCheck).length,
    maxPairCapacityDelta: config.internalCapWarn,
    pairConstraintViolations: blocks.filter(b => b.deltaCapacity > config.internalCapWarn).length,
  };
}

/* ── Construction d'un pack a partir d'une solution ─────────────────────── */

function buildPack(cells, sol, config, ctx, salt) {
  const P = sol.pa.length;
  const rawBlocks = [];
  for (let k = 0; k < P; k++) rawBlocks.push(computeBlock(cells[sol.pa[k]], cells[sol.pb[k]], config));

  // Placement physique : permutation optimisee apres validation des paires.
  const placement = optimizePlacement(rawBlocks, mulberry32(ctx.seedBase + 9000 + salt));
  const byStrength = [...rawBlocks].sort((a, b) => blockPerformance(b) - blockPerformance(a));
  byStrength.forEach((blk, k) => { blk.strengthRank = k + 1; });
  const physical = placement.perm.map(i => rawBlocks[i]);
  physical.forEach((blk, k) => {
    const p = k + 1; // numerotation depuis le NEGATIF
    blk.physicalPosition = p;
    blk.blockNumber = p;
    blk.positionFromNegative = [2 * p - 1, 2 * p];
  });

  const flatCells = [];
  physical.forEach(blk => { flatCells.push(blk.cellA, blk.cellB); });

  const stats = packStats(physical, placement, config);
  const alerts = [];
  if (stats.verdict === "Non equilibre") alerts.push({ level: "critical", text: stats.warning });
  physical.forEach(b => b.alerts.filter(a => a.level !== "info").forEach(a => alerts.push({ ...a, text: `Bloc ${b.blockNumber} — ${a.text}` })));

  return {
    success: true,
    packSize: config.packSize,
    totalBlocks: P,
    blocks: physical,
    cells: flatCells,
    stats,
    alerts,
    avgCapacity: stats.avgCellCapacity,
    cellIds: new Set(flatCells.map(c => c.id)),
    placementScore: stats.placementScore,
    metrics: sol.m,
  };
}

/* ── Propositions A / B / C ─────────────────────────────────────────────── */

function overlap(a, b) {
  let common = 0;
  for (const id of a.cellIds) if (b.cellIds.has(id)) common++;
  return common / a.cellIds.size;
}

export function generateScenarios(inputCells, options = {}) {
  const t0 = Date.now();
  const config = buildConfig(options);
  const cells = prepareCells(inputCells, config);
  const n = cells.length;
  if (n < config.packSize) {
    throw new Error(`Pas assez de cellules DISPO. Necessaire: ${config.packSize}, disponible: ${n}`);
  }
  const P = config.blocks;
  const counters = { candidates: 0, iterations: 0, evaluations: 0 };
  const ctx = { cells, n, P, config, counters, usable: new Uint8Array(n).fill(1), seedBase: hashSeed(cells, config, 1) };
  const scratch = newMetrics();
  if (config.debug) console.debug(`[balancer] ${n} cellules disponibles, pack ${config.packSize}`);

  const usableIdx = (u) => { const a = []; for (let i = 0; i < n; i++) if (u[i]) a.push(i); return a; };
  const balanceMode = makeMode("balance");

  // ── PACK A : meilleur equilibre ────────────────────────────────────────
  const idxAll = usableIdx(ctx.usable);
  const rankedA = rankWindows(cells, idxAll, P, balanceMode, scratch);
  const seedsA = pickSeeds(rankedA, BUDGET.seedsA, Math.max(2, Math.floor(n / 60))).map(w => w.seed);
  const solA = searchPack(ctx, balanceMode, seedsA, BUDGET.itersA, 1);
  const packs = [];
  const packA = buildPack(cells, solA, config, ctx, 1);
  packs.push({ id: "A", title: "Pack A — Meilleur equilibre", tier: "Pack A", label: "Meilleur equilibre", pack: packA });

  // ── PACK B : capacite elevee sous contrainte d'equilibre ────────────────
  const capMode = makeMode("capacity", solA.m, config.packSize);
  const rankedB = rankedA.map(w => ({ ...w, e: -avgCapOf(cells, idxAll, w.start, P) }));
  const seedsB = pickSeeds(rankedB, BUDGET.seedsB, Math.max(2, Math.floor(n / 40))).map(w => w.seed);
  const solB = searchPack(ctx, capMode, seedsB, BUDGET.itersB, 2, [{ pa: solA.pa, pb: solA.pb }]);
  const packB = buildPack(cells, solB, config, ctx, 2);

  // ── PACK C : alternative reellement differente (cellules du stock restant) ──
  let packC = null;
  const usableC = new Uint8Array(n).fill(1);
  const inA = new Set(packA.cellIds);
  const forbidAll = n - config.packSize >= config.packSize;
  cells.forEach((c, i) => { if (inA.has(c.id)) usableC[i] = 0; });
  let usableForC = usableC;
  if (!forbidAll) { // stock insuffisant pour un pack disjoint : on interdit une cellule sur deux de A
    usableForC = new Uint8Array(n).fill(1);
    let flip = false;
    cells.forEach((c, i) => { if (inA.has(c.id)) { if (flip) usableForC[i] = 0; flip = !flip; } });
  }
  const idxC = usableIdx(usableForC);
  if (idxC.length >= config.packSize) {
    const ctxC = { ...ctx, usable: usableForC, seedBase: hashSeed(cells, config, 3) };
    const rankedC = rankWindows(cells, idxC, P, balanceMode, scratch);
    const seedsC = pickSeeds(rankedC, BUDGET.seedsC, Math.max(2, Math.floor(idxC.length / 40))).map(w => w.seed);
    if (seedsC.length) {
      const solC = searchPack(ctxC, balanceMode, seedsC, BUDGET.itersC, 3);
      packC = buildPack(cells, solC, config, ctx, 3);
    }
  }

  // ── Filtrage : pas de doublons, pas de mauvais pack "pour faire nombre" ──
  const rankOf = (v) => ["Excellent", "Tres bon", "Bon", "A revoir", "Non equilibre"].indexOf(v);
  const maxRank = Math.max(rankOf(packA.stats.verdict), rankOf("Bon"));
  const accept = (p, others) => p
    && rankOf(p.stats.verdict) <= maxRank
    && others.every(o => overlap(p, o) < 0.9);

  const shown = [packs[0]];
  if (accept(packB, [packA])) {
    shown.push({ id: "B", title: "Pack B — Capacite elevee", tier: "Pack B", label: "Capacite elevee", pack: packB });
  }
  if (accept(packC, shown.map(s => s.pack))) {
    shown.push({ id: "C", title: "Pack C — Alternative", tier: "Pack C", label: "Alternative", pack: packC });
  }

  const search = { cellsAvailable: n, candidates: counters.candidates, iterations: counters.iterations, evaluations: counters.evaluations, ms: Date.now() - t0 };
  if (config.debug) console.debug("[balancer] termine", search);

  return shown.map((s) => {
    const why = explain(s, shown);
    return {
      id: s.id, tier: s.tier, title: s.title, label: s.label,
      ...omitInternal(s.pack),
      why,
      analysis: buildAnalysis(s.pack, n),
      search,
    };
  });
}

function omitInternal(p) {
  const { cellIds, metrics, ...rest } = p; // eslint-disable-line no-unused-vars
  return rest;
}

function avgCapOf(cells, idx, start, P) {
  let s = 0;
  for (let k = 0; k < 2 * P; k++) s += cells[idx[start + k]].Cap_Ah;
  return s / (2 * P);
}

// Bilan du stock : pourquoi certaines cellules disponibles ne peuvent pas entrer dans le calcul.
export function describeStock(inputCells, options = {}) {
  const config = buildConfig(options);
  const norm = inputCells.map(normalizeCell);
  const avail = norm.filter(c => !config.onlyAvailable || String(c.Disponible).toUpperCase().startsWith("DISPO"));
  const bySeries = (list) => list.reduce((acc, c) => { const k = c.Serie || "?"; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const noMeasure = avail.filter(c => !(isValidNumber(c.Cap_Ah) && c.Cap_Ah > 0 && isValidNumber(c.Resistance) && c.Resistance > 0));
  const usable = prepareCells(inputCells, config);
  const measured = avail.length - noMeasure.length;
  const belowMin = config.minCapacity != null
    ? avail.filter(c => isValidNumber(c.Cap_Ah) && c.Cap_Ah > 0 && isValidNumber(c.Resistance) && c.Resistance > 0 && c.Cap_Ah < config.minCapacity).length
    : 0;
  return {
    available: avail.length,
    usable: usable.length,
    noMeasure: { count: noMeasure.length, series: bySeries(noMeasure) },
    duplicates: Math.max(0, measured - belowMin - usable.length),
    belowMin,
  };
}

// Pack unique (compat) : le meilleur equilibre.
export function generateBatteryPack(inputCells, options = {}) {
  return generateScenarios(inputCells, options)[0];
}

/* ── Explications ───────────────────────────────────────────────────────── */

function explain(entry, all) {
  const s = entry.pack.stats;
  const A = all[0].pack.stats;
  const crit = s.criticalBlocks === 0 ? "aucun bloc critique" : `${s.criticalBlocks} bloc(s) critique(s)`;
  const base = `delta global ${s.capacityTotal.delta} mAh, pire delta interne ${s.internalCap.max} mAh, delta IR ${s.irTotal.delta}, ${crit}`;
  if (entry.id === "A") {
    const lines = [`Pack recommande : meilleur equilibre trouve parmi les solutions testees — ${base}.`];
    for (const o of all.slice(1)) {
      const os = o.pack.stats;
      lines.push(`Prefere au ${o.title.split(" —")[0]} : delta global ${s.capacityTotal.delta} contre ${os.capacityTotal.delta} mAh, pire delta interne ${s.internalCap.max} contre ${os.internalCap.max} mAh (capacite moyenne cellule ${s.avgCellCapacity} contre ${os.avgCellCapacity} mAh : la capacite ne prime jamais sur l'homogeneite).`);
    }
    return lines;
  }
  if (entry.id === "B") {
    const gain = round(s.avgCellCapacity - A.avgCellCapacity, 1);
    return [`Privilegie la capacite : ${s.avgCellCapacity} mAh en moyenne par cellule (${gain >= 0 ? "+" : ""}${gain} mAh vs Pack A) tout en gardant un equilibrage acceptable — ${base}.`,
            `Compromis vs Pack A : delta global ${s.capacityTotal.delta - A.capacityTotal.delta >= 0 ? "+" : ""}${round(s.capacityTotal.delta - A.capacityTotal.delta, 0)} mAh.`];
  }
  const shared = Math.round(overlap(entry.pack, all[0].pack) * 100);
  return [`Combinaison differente du Pack A (${shared} % de cellules en commun) pour garder du stock de qualite — ${base}.`];
}

function buildAnalysis(pack, available) {
  const s = pack.stats;
  const lines = [];
  if (s.warning) lines.push(`⚠ ${s.warning}.`);
  lines.push(
    `Selection de ${pack.packSize} cellules parmi ${available} disponibles — capacite moyenne cellule ${s.avgCellCapacity} mAh, moyenne bloc ${s.avgBlockCapacity} mAh.`,
    `PRIORITE 1 — Equilibre global des ${pack.totalBlocks} blocs : delta ${s.capacityTotal.delta} mAh (bloc ${s.weakestBlock.blockNumber} ${s.weakestBlock.capacity} / bloc ${s.strongestBlock.blockNumber} ${s.strongestBlock.capacity}).`,
    `PRIORITE 2-3 — Deltas internes : min ${s.internalCap.min} / moyen ${s.internalCap.mean} / max ${s.internalCap.max} mAh.`,
    `PRIORITE 4-5 — IR : delta entre blocs ${s.irTotal.delta}, delta interne moyen ${s.internalIR.mean} / max ${s.internalIR.max}.`,
    `PRIORITE 6-7 — Placement : score ${s.placementScore}, plus gros saut entre voisins ${s.maxNeighborJump} mAh.`,
    `Chaque cellule n'est utilisee qu'une fois ; temps de decharge et Etat ne comptent pas dans le score.`,
  );
  if (s.measureChecks > 0) lines.push(`${s.measureChecks} bloc(s) avec "Mesure a verifier" (information seulement).`);
  lines.push(`Verdict : ${s.verdict} — qualite ${s.qualityPercent} %.`);
  return lines;
}

/* ── Aides positionnelles ───────────────────────────────────────────────── */

export function getBlockPosition(blockNumber, cellsPerBlock = 2) {
  const startCell = (blockNumber - 1) * cellsPerBlock + 1;
  const endCell = startCell + cellsPerBlock - 1;
  return {
    blockNumber,
    cells: [startCell, endCell],
    text: `Bloc ${blockNumber} = cellules ${startCell} et ${endCell} depuis le (-)`,
  };
}

export function getCellBlock(cellPosition, cellsPerBlock = 2) {
  return {
    cellPosition,
    blockNumber: Math.ceil(cellPosition / cellsPerBlock),
    positionInBloc: cellPosition % 2 === 0 ? 2 : 1,
  };
}

/* ── Utilitaires ────────────────────────────────────────────────────────── */

function average(arr) { return sum(arr) / arr.length; }
function sum(arr) { return arr.reduce((a, b) => a + b, 0); }
function min(arr) { return Math.min(...arr); }
function max(arr) { return Math.max(...arr); }
function round(value, decimals = 2) { return Number(value.toFixed(decimals)); }
function isValidNumber(value) { return typeof value === "number" && !Number.isNaN(value) && Number.isFinite(value); }

// Exposes pour les tests unitaires uniquement.
export const __internals = { evaluate, newMetrics, cmpBalance, prepareCells, buildConfig };
